import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Accordion } from "react-bootstrap";
import axios from "axios";
import { AppCard, AppInput, AppRemixIcon } from "@/layout/AppComponents";
import { Formik } from "formik";
import { useParams, useNavigate } from "react-router-dom";
import ReactDOMServer from "react-dom/server";
import Sidebar from "../../../layout/Sidebar/Sidebar";
import ContentLoader from "../../../layout/components/Loader/ContentLoader";
import NoInfo from "../../../layout/components/helpers/NoInfo";
import PlantCardsSwitch from "../../../layout/screenSpecifics/Modules/LegalCompliance/PlantSelect/PlantCardSwitch/PlantCardsSwitch";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import { axiosPath, path } from "../../../routing/helpers/router";
import * as currentPageAction from "../../../store/actions/currentPageAction";
import AppLink from "../../../routing/helpers/AppLink";
import Translator from "../../../layout/components/helpers/Translator";
import PlantAutoCloneElement from "../../../layout/screenSpecifics/Modules/LegalCompliance/PlantSelect/PlantCardSwitch/PlantAutoCloneElement";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import _ from "lodash";

const LegalCompliancePlantSelectHomeScreen = () => {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const auth = useSelector((state) => state.auth);
  const global = useSelector((state) => state.global);
  const permissions = useSelector((state) => state.currentPage?.permissions);
  const userBridgePermissions = useSelector(
    (state) => state.currentPage?.userBridgePermissions,
  );
  const moduleBridgeSettings = useSelector(
    (state) => state.currentPage?.moduleBridgeSettings,
  );
  const isSidebarOpened = useSelector(
    (state) => state.auth?.settings?.preferences?.sidebar?.isOpened,
  );
  const [module, setModule] = useState(null);
  const [moduleExtended, setModuleExtended] = useState(null);
  const [filteredPlants, setFilteredPlants] = useState(null);
  const [isDownloadingCustomWelcomeFile, setIsDownloadingCustomWelcomeFile] =
    useState(false);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  const loadModule = useCallback(async (mode) => {
    await axios
      .post(
        axiosPath("api_r_user_module_legalCompliance_plantSelect_loadModule"),
        {
          moduleBridgeHex,
          mode,
        },
      )
      .then(({ data }) => {
        if (isMounted.current) {
          if (mode == "base") {
            setModule(data);
            dispatch(
              currentPageAction.change("permissions", data?.permissions),
            );
            dispatch(
              currentPageAction.change(
                "userBridgePermissions",
                data.userBridgePermissions,
              ),
            );
            dispatch(
              currentPageAction.change(
                "moduleBridgeSettings",
                data.moduleBridgeSettings,
              ),
            );
          }

          if (mode == "extended") {
            if (isMounted.current) setModuleExtended(data);
          }
        }
      })
      .catch(() => {
        navigate(
          path({
            name: "user_home",
          }),
        );
      });
  }, []);

  useEffect(() => {
    loadModule("base");
  }, [moduleBridgeHex]);

  useEffect(() => {
    if (module) {
      loadModule("extended");
      setFilteredPlants(module.plants);
    }
  }, [module]);

  const handleFilterByNameDebounce = (v) => {
    let tempFilteredPlants = { ...module.plants };
    tempFilteredPlants = _.filter(tempFilteredPlants, (p) =>
      p.name.toUpperCase().includes(v.text.toUpperCase()),
    );
    setFilteredPlants(tempFilteredPlants);
  };

  const handleDownloadCustomWelcomeFile = async () => {
    if (isDownloadingCustomWelcomeFile) return;
    setIsDownloadingCustomWelcomeFile(true);
    await axios
      .post(
        axiosPath(
          "api_r_user_module_legalCompliance_plantSelect_downloadWelcomeCustomFile",
        ),
        { moduleBridgeHex },
      )
      .then((r) => window.open(r.data.s3FileUrl, "_blank"))
      .finally(() => setIsDownloadingCustomWelcomeFile(false));
  };

  return (
    <div>
      <BreadcrumbHandler paths={[]} />
      <Sidebar>
        <img
          src={auth.user?.client?.logoUrl}
          className="img-fluid"
          alt={auth.user?.client?.name}
          title={auth.user?.client?.name}
        />
        <Accordion>
          <ul>
            {permissions?.gcl?.module?.plant?.calendar
              ?.customEmailNotifications &&
              userBridgePermissions?.permissions?.email && (
                <li className="sidebar-element">
                  <AppLink
                    path="user_module_legalCompliance_pendingEmails"
                    params={{ moduleBridgeHex }}
                    className="sidebar-element"
                  >
                    <AppRemixIcon icon="mail-send" className="sidebar-icon" />{" "}
                    <span className="sb-title">
                      <Translator
                        file="module-legalCompliance"
                        name="pending.emails"
                      />
                    </span>
                  </AppLink>
                </li>
              )}
            {permissions?.gcl?.module?.plant?.calendar
              ?.customEmailNotifications &&
              userBridgePermissions?.permissions?.email && (
                <li>
                  <AppLink
                    path="user_module_legalCompliance_configuration"
                    params={{ moduleBridgeHex }}
                    className="sidebar-element"
                  >
                    <AppRemixIcon
                      icon="mail-settings"
                      className="sidebar-icon"
                    />{" "}
                    <span className="sb-title">
                      <Translator
                        file="module-legalCompliance"
                        name="settings"
                      />
                    </span>
                  </AppLink>
                </li>
              )}
            <li className="divider" />
            <li>
              {moduleBridgeSettings?.customWelcomeFile ? (
                <span
                  className="sidebar-element"
                  onClick={handleDownloadCustomWelcomeFile}
                >
                  <AppRemixIcon
                    icon={
                      isDownloadingCustomWelcomeFile
                        ? "loader-4"
                        : "questionnaire"
                    }
                    className="sidebar-icon"
                  />{" "}
                  <span className="sb-title">
                    <Translator
                      file="module-legalCompliance"
                      name="worldlex.manual"
                    />
                  </span>
                </span>
              ) : (
                <a
                  href={
                    global.urlData?.lang &&
                    ["es", "en", "fr", "pt", "it", "ar", "de"].includes(
                      global.urlData?.lang,
                    )
                      ? `https://worldlex.net/assets/doc/worldlex2026-${global.urlData?.lang}.pdf`
                      : "https://worldlex.net/assets/doc/worldlex2026-en.pdf"
                  }
                  className="sidebar-element"
                  target="_blank"
                  rel="noreferrer"
                >
                  <AppRemixIcon icon="questionnaire" className="sidebar-icon" />{" "}
                  <span className="sb-title">
                    <Translator
                      file="module-legalCompliance"
                      name="worldlex.manual"
                    />
                  </span>
                </a>
              )}
            </li>
            {module?.autoClones?.map(
              (ac) =>
                ac.isActive && (
                  <PlantAutoCloneElement autoClone={ac} key={ac.id} />
                ),
            )}
          </ul>
        </Accordion>
      </Sidebar>
      <div className="row">
        <main role="main" className="col-md-12 col-lg-12 px-md-4">
          <div className={`${isSidebarOpened && "sidebar-expanded"}`}>
            <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-0 mb-0 border-bottom">
              {/* chatBotGCLModule chatBotGCLPlant*/}
              {![621, 548, 1122, 504, 220].includes(auth?.user?.client?.id) &&
                (permissions?.gcl?.module?.chatBotGCLModule ||
                  permissions?.gcl?.module?.plant?.chatBotGCLPlant ||
                  permissions?.gcl?.module?.userQuery) && (
                  <div className="alert alert-info">
                    <h3>
                      <Translator
                        file="module-legalCompliance"
                        name="news2026.title"
                      />
                    </h3>
                    {(permissions?.gcl?.module?.chatBotGCLModule ||
                      permissions?.gcl?.module?.plant?.chatBotGCLPlant) && (
                      <p>
                        <Translator
                          file="module-legalCompliance"
                          name="news2026.chatbot"
                          isHtml
                        />
                      </p>
                    )}
                    {permissions?.gcl?.module?.userQuery && (
                      <p>
                        <Translator
                          file="module-legalCompliance"
                          name="news2026.userQuery"
                          isHtml
                        />
                      </p>
                    )}
                  </div>
                )}
              {/* {![621, 548, 1122, 504, 220].includes(auth?.user?.client?.id) &&
                (permissions?.global?.chatBot ||
                  permissions?.gcl?.module?.userQuery) && (
                  <div className="alert alert-info">
                    <h3>
                      <Translator
                        file="module-legalCompliance"
                        name="news2026.title"
                      />
                    </h3>
                    {permissions?.global?.chatBot && (
                      <p>
                        <Translator
                          file="module-legalCompliance"
                          name="news2026.chatbot"
                          isHtml
                        />
                      </p>
                    )}
                    {permissions?.gcl?.module?.userQuery && (
                      <p>
                        <Translator
                          file="module-legalCompliance"
                          name="news2026.userQuery"
                          isHtml
                        />
                      </p>
                    )}
                  </div>
                )} */}
              {!["es", "en", "de", "fr", "pt", "it", "ar", "tl", "wm"].includes(
                global?.urlData?.lang,
              ) && (
                <div className="alert alert-info">
                  <Translator
                    file="module-legalCompliance"
                    name="welcome-new-worldlexnet-currently-working-translation-language"
                  />
                </div>
              )}
              <AppCard bodyClassName="p-1">
                <Formik
                  validateOnChange={false}
                  validateOnBlur={false}
                  enableReinitialize
                  initialValues={{
                    text: "",
                  }}
                  onSubmit={(v) => handleFilterByNameDebounce(v)}
                >
                  {({ setFieldValue, resetForm, handleSubmit, values }) => (
                    <AppInput
                      title={ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="client.plant.name"
                        />,
                      )}
                      icon={<AppRemixIcon icon="tools" />}
                      placeholder={`${ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="filter.plants"
                        />,
                      )}... (${ReactDOMServer.renderToString(
                        <Translator
                          file="module-legalCompliance"
                          name="enter.filter.esc.delete.search"
                        />,
                      )})`}
                      value={values.text}
                      onChange={(v) => setFieldValue("text", v)}
                      onPressKey={(v) => {
                        v === "Enter" ? handleSubmit() : resetForm();
                        handleSubmit();
                      }}
                      onPressKeyList={["Enter", "Escape"]}
                    />
                  )}
                </Formik>
              </AppCard>
              <div className="row">
                <div className="col-lg-12">
                  {filteredPlants ? (
                    filteredPlants.length === 0 ? (
                      filteredPlants.length === 0 && (
                        <div className="card">
                          <div className="card-body">
                            <NoInfo
                              icon={
                                <AppRemixIcon
                                  icon="search-eye"
                                  color="#ccc"
                                  size={34}
                                />
                              }
                              text={ReactDOMServer.renderToString(
                                <Translator
                                  file="module-legalCompliance"
                                  name="plants.not.found"
                                />,
                              )}
                            />
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="card">
                        <div className="card-body">
                          {_.map(filteredPlants, (plant) => (
                            <PlantCardsSwitch
                              key={plant.hex}
                              plant={plant}
                              plantExtended={
                                moduleExtended &&
                                moduleExtended.plants[plant.hex]
                                  ? moduleExtended.plants[plant.hex]
                                  : null
                              }
                              moduleBridgeHex={moduleBridgeHex}
                            />
                          ))}
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="card">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-lg-7 col-md-12 col-sm-12 col-12">
                            <ContentLoader
                              type="line"
                              numElements={2}
                              offset={{
                                l: 0,
                                r: 30,
                                t: 10,
                                b: 0,
                              }}
                            />
                          </div>
                          <div className="col-lg-3 col-md-6 col-sm-6 col-6">
                            <ContentLoader
                              type="line"
                              numElements={2}
                              offset={{
                                l: 0,
                                r: 30,
                                t: 10,
                                b: 0,
                              }}
                            />
                          </div>
                          <div className="col-lg-2 col-md-6 col-sm-6 col-6">
                            <div className="btn-group float-right">
                              <ContentLoader
                                type="buttons"
                                numElements={2}
                                right
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default LegalCompliancePlantSelectHomeScreen;
